import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import * as _ from 'underscore';
import { Observable } from 'rxjs';
import { DecimalPipe } from '@angular/common';
import { BasePaylineEditComponent } from '../base-payline-edit/base-payline-edit.component';
import { EmployeeDirectDeposit } from '../../../../models/employee-direct-deposit';
import { DirectDeposit } from '../../../../models/payroll';

@Component({
  selector: 'payline-direct-deposit-row',
  templateUrl: './payline-direct-deposit-row.component.html',
  styleUrls: ['./payline-direct-deposit-row.component.css'],
  standalone: false
})
export class PaylineDirectDepositRowComponent extends BasePaylineEditComponent implements OnInit {
  @Input() employeeDirectDeposit: EmployeeDirectDeposit;
  @Input() directDeposit: DirectDeposit;
  @Input() clearDepositsEvent: Observable<void>;
  @Input() rowIndex: number;
  @Input() lastRowIndex: number;
  @Output() onEdit: EventEmitter<DirectDeposit> = new EventEmitter<DirectDeposit>();

  // form group to manage our user inputs
  public directDepositForm = new UntypedFormGroup({
    amount: new UntypedFormControl('')
  });

  constructor(private decimalPipe: DecimalPipe) {
    super();
  }

  // NOTE: object reference is broken from the payroll data model.
  // when changes to the current amount are detected,
  // (from Preview Totals), update the total field
  ngDoCheck() {
    // this input is different than the other totals, as it can be edited.
    // in this case, if there is any temporary override set,
    // do not change the input at all. it DOES need to update when CurrentAmount
    // changes (which happens when earnings, deductions, etc are updated)
    if (this.directDeposit.TemporaryAmount || this.directDeposit.TemporaryAmount === 0) {
      return;
    }

    let value = this.directDepositForm.get('amount').value || null;
    // CurrentAmount can be null or 0
    let currentAmount = this.directDeposit.CurrentAmount || null;
    if (currentAmount != value) {
      this.directDepositForm.get('amount').setValue(this.directDeposit.CurrentAmount);
    }
  }

  ngOnInit(): void {
    this.clearDepositsEvent.subscribe(() => {
      this.directDeposit.TemporaryAmount = 0;
      this.directDeposit.CurrentAmount = 0; // so total field updates immediately

      this._setDirectDepositValues(this.directDeposit);
    });

    this._configureControls();
    this._setDirectDepositValues(this.directDeposit);
  }

  // #region Public Methods

  public getCalculationType(calculationType) {
    switch (calculationType) {
      case 0:
        let dollarAmount = this.decimalPipe.transform(
          this.employeeDirectDeposit.Amount ?? 0,
          '1.0-2'
        );

        return `$${dollarAmount}`;
      case 1:
        let percentOfNetAmount = this.decimalPipe.transform(
          this.employeeDirectDeposit.Amount ?? 0,
          '1.0-2'
        );

        return `${percentOfNetAmount}% of Net`;
      case 2:
        return 'Remaining Net';
    }
    return '';
  }

  public getMaskedAcctNo(acctNo) {
    // NOTE: this should already be coming from the server masked,
    // but this is another safety measure and will ensure that all
    // account numbers appear uniform with 8 characters
    const a = acctNo.toString();
    return a
      .slice(a.length - 4)
      .padStart(4, '0')
      .padStart(8, '*');
  }

  public onAmountChange(evnt): void {
    // attempt to let users to keyboard navigate in the detail editor
    if (this.navigationKeys.includes(evnt.code)) {
      this.onKeyboardNavigate(
        evnt.code,
        this.rowIndex,
        5, // amount column is at focusable column index 5
        this.lastRowIndex
      );
    }

    this._onAmountChange(evnt);
  }

  // #endregion

  // #region Private Methods

  private _configureControls() {}

  private _onAmountChange(evnt) {
    let value = evnt?.target?.value || 0;

    // bug 14403. do  not set TemporaryAmount unless value actually changed
    // the direct deposit CurrentAmount will update after calculation when
    // earnings and deductions are changed. if we set a temporary amount when the user
    // did not change it, this can throw off final calculations since.
    if (this.directDeposit.TemporaryAmount == null && this.directDeposit.CurrentAmount == +value) {
      // do nothing!
    } else {
      this.directDeposit.TemporaryAmount = value;
      this.onEdit.emit(this.directDeposit);
    }
  }

  private _setDirectDepositValues(directDeposit: DirectDeposit): void {
    this.directDepositForm
      .get('amount')
      // NOTE: use ?? rather than || to ensure TemporaryAmount's of 0 override permanent/current amounts
      // if temp or current amount is not set, default to the employee's direct deposit amount
      .setValue(directDeposit.TemporaryAmount ?? directDeposit.CurrentAmount);
  }

  // #endregion
}
